import { Button } from '@/shared/components/ui/button'
import { Dialog, DialogContent } from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@/shared/components/ui/form'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Typography } from '@/shared/components/ui/typography'
import { useWorkspace } from '@/shared/hooks/data-hooks/workspaces/useWorkspace'
import { useMoneyFormatter } from '@/shared/hooks/useFormatters'
import { usePlans } from '@/shared/hooks/usePlans'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import { cn } from '@/shared/lib/utils'
import { SubscriptionPlanType } from '@/types/subscription'
import { zodResolver } from '@hookform/resolvers/zod'
import { DialogProps } from '@radix-ui/react-dialog'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { useFeatureFlags } from '../hooks/useFeatureFlags'

export type FormValues = z.infer<typeof formSchema>
export type UpgradePlanModalVariant = 'limited_playbooks' | 'limited_feature'

type UpgradePlanModalProps = Omit<DialogProps, 'onSubmit' | 'onOpenChange'> & {
  /**
   * Callback when form is submitted with selected plan or when the user clicks "Maybe later" / closes the dialog
   */
  onSubmit: (data?: FormValues) => void | Promise<void>
  variant: UpgradePlanModalVariant
}

export function UpgradePlanModal({
  onSubmit,
  variant = 'limited_feature',
  ...dialogProps
}: UpgradePlanModalProps) {
  const { workspaceId, isLoading: isLoadingWorkspaceContext } =
    useWorkspaceContext()
  const { data: { data: workspace } = {}, isLoading: isLoadingWorkspace } =
    useWorkspace(workspaceId)
  const plans = usePlans()
  const { formatCurrencyValueToParts } = useMoneyFormatter()

  const [isProPlanEnabled, isTeamsPlanEnabled] = useFeatureFlags([
    'plansProPlan',
    'plansTeamsPlan',
  ])

  const formSchema = z.object({
    planType: z.enum(
      [
        isProPlanEnabled ? SubscriptionPlanType.Pro : undefined,
        isTeamsPlanEnabled ? SubscriptionPlanType.Team : undefined,
      ].filter(Boolean) as SubscriptionPlanType[]
    ),
  })

  const currentSubscriptionPlanType =
    workspace?.subscription?.subscriptionPlan?.type
  const isLoading = isLoadingWorkspace || isLoadingWorkspaceContext

  const proPlan = plans.find((plan) => plan.id === 'pro')
  const teamsPlan = plans.find((plan) => plan.id === 'teams')

  const modalDescriptions = {
    limited_playbooks: {
      title: "You've reached your free plan limit.",
      description:
        "You've reached the limit of playbooks on your plan. Upgrade now to continue creating without limits.",
    },
    limited_feature: {
      title: 'Unlock superpowers.',
      description: 'Upgrade your plan today and unlock special features',
    },
  }

  const planCards = useMemo(
    () =>
      [
        proPlan && isProPlanEnabled
          ? {
              planType: SubscriptionPlanType.Pro,
              title: 'Upgrade to Pro',
              description: proPlan?.description,
              price: proPlan?.price,
              width: 'w-[215px]',
            }
          : null,
        teamsPlan && isTeamsPlanEnabled
          ? {
              planType: SubscriptionPlanType.Team,
              title: 'Upgrade to Teams',
              description: teamsPlan?.description,
              price: teamsPlan?.price,
              width: 'w-[216px]',
            }
          : null,
      ].filter(Boolean),
    [proPlan, teamsPlan, isProPlanEnabled, isTeamsPlanEnabled]
  )

  const currentPlanIndex = useMemo(
    () =>
      planCards.findIndex((pc) => pc.planType === currentSubscriptionPlanType),
    [planCards, currentSubscriptionPlanType]
  )

  const availablePlans = useMemo(
    () =>
      planCards.filter((planCard, index) => {
        return !currentSubscriptionPlanType
          ? true
          : planCard.planType !== currentSubscriptionPlanType &&
              index > currentPlanIndex
      }),
    [planCards, currentSubscriptionPlanType, currentPlanIndex]
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: {
      planType: availablePlans.at(0)?.planType,
    },
    disabled: isLoading,
  })

  const {
    formState: { isSubmitting },
    watch,
  } = form

  const selectedPlanType = watch('planType')

  const highlightedPlanData =
    selectedPlanType === SubscriptionPlanType.Team ? teamsPlan : proPlan
  const featuresPlaceholder = highlightedPlanData?.features || []

  const handleMaybeLater = () => {
    onSubmit?.()
  }

  const handleSubmit = async (data: FormValues) => {
    await onSubmit(data)
  }

  const hasNoAvailablePlansToSelect = !isLoading && !availablePlans.length

  if (!proPlan || !teamsPlan) {
    return null
  }

  return (
    <Dialog {...dialogProps} onOpenChange={handleMaybeLater}>
      <DialogContent className="max-w-[482px] p-4">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            {/* Gradient Header with CTA Button */}
            {!hasNoAvailablePlansToSelect && (
              <div
                className={cn(
                  selectedPlanType === SubscriptionPlanType.Pro
                    ? 'bg-plan-bg-rotated-90'
                    : 'bg-plan-bg',
                  'relative mt-8 flex h-[171px] items-center justify-center overflow-hidden rounded-xl'
                )}
                style={{
                  ...(selectedPlanType === SubscriptionPlanType.Pro
                    ? {
                        backgroundPosition: '29% 27%',
                      }
                    : {
                        backgroundPosition: '70% 58%',
                        backgroundSize: '107% 409%',
                      }),
                }}
              >
                {/* CTA Button */}
                {isLoading ? (
                  <div className="relative z-10 rounded-xl border border-black bg-gradient-to-b from-white/100 to-white/10 px-2 py-2">
                    <Skeleton className="h-[44px] w-[180px]" />
                  </div>
                ) : (
                  <div className="relative z-10 rounded-xl border border-black bg-gradient-to-b from-white/100 to-white/10 px-2 py-2">
                    <Typography
                      className="text-[40px] font-normal leading-[44px] tracking-tight"
                      style={{ fontFamily: 'GT Planar, sans-serif' }}
                    >
                      {selectedPlanType === SubscriptionPlanType.Team
                        ? 'Get Teams'
                        : 'Get Pro'}
                    </Typography>
                  </div>
                )}
              </div>
            )}

            {/* Title and Description */}
            <div className="flex flex-col gap-1">
              <Typography variant="subtitle" className="font-semibold">
                {modalDescriptions[variant].title}
              </Typography>
              <Typography variant="body1" className="text-mydra-gray">
                {modalDescriptions[variant].description}
              </Typography>
            </div>

            {/* Plan Cards */}
            {isLoading ? (
              <div className="flex gap-2">
                {/* Skeleton for Pro Plan */}
                <div className="flex w-[215px] flex-col gap-3 rounded-lg p-2">
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Skeleton className="h-9 w-24" />
                  </div>
                </div>
                {/* Skeleton for Team Plan */}
                <div className="flex w-[216px] flex-col gap-3 rounded-lg p-2">
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-6 w-36" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Skeleton className="h-9 w-24" />
                  </div>
                </div>
              </div>
            ) : (
              <FormField
                control={form.control}
                name="planType"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex gap-2">
                        {availablePlans.map((plan) => {
                          const priceParts = formatCurrencyValueToParts(
                            plan.price.amount.toString(),
                            plan.price.currency
                          )
                          const currencySymbol = priceParts.find(
                            (part) => part.type === 'currency'
                          )?.value
                          const integerValue = priceParts.find(
                            (part) => part.type === 'integer'
                          )?.value
                          const isSelected = field.value === plan.planType
                          const isCurrentSubscriptionPlanType =
                            currentSubscriptionPlanType === plan.planType
                          const isDisabled = isCurrentSubscriptionPlanType

                          return (
                            <button
                              key={plan.planType}
                              type="button"
                              onClick={() =>
                                !isDisabled && field.onChange(plan.planType)
                              }
                              disabled={isDisabled}
                              className={cn(
                                'flex flex-col gap-3 rounded-lg p-2 text-left transition-colors',
                                plan.width,
                                isSelected
                                  ? 'bg-secondary-lighter'
                                  : 'bg-transparent hover:bg-gray-50',
                                isDisabled && 'cursor-not-allowed opacity-60'
                              )}
                            >
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <Typography
                                    variant="body1"
                                    className="font-semibold leading-6"
                                  >
                                    {plan.title}
                                  </Typography>
                                  {isCurrentSubscriptionPlanType && (
                                    <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold">
                                      Current
                                    </span>
                                  )}
                                </div>
                                <Typography
                                  variant="body2"
                                  className={cn(
                                    'text-left',
                                    isSelected
                                      ? 'text-mydra-black'
                                      : 'text-mydra-gray'
                                  )}
                                >
                                  {plan.description}
                                </Typography>
                              </div>
                              <div className="flex items-center gap-0.5">
                                <span className="text-lg font-medium leading-[22.5px] tracking-tight">
                                  {currencySymbol}
                                </span>
                                <span className="text-[30px] font-normal leading-[33px] tracking-tight">
                                  {integerValue}
                                </span>
                                <Typography
                                  variant="caption"
                                  className={cn(
                                    'text-[10px] font-semibold',
                                    isSelected
                                      ? 'text-mydra-black'
                                      : 'text-mydra-gray'
                                  )}
                                >
                                  / billed monthly
                                </Typography>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {!hasNoAvailablePlansToSelect ? (
              <>
                <div className="py-2">
                  {isLoading ? (
                    <div className="space-y-2 pl-6">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-5 w-2/3" />
                      <Skeleton className="h-5 w-5/6" />
                      <Skeleton className="h-5 w-4/5" />
                    </div>
                  ) : (
                    <ul className="list-disc space-y-0 pl-6 text-sm leading-[22px]">
                      {featuresPlaceholder.map((feature, index) => (
                        <li key={index} className="text-sm">
                          {feature.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-[219px]"
                    onClick={handleMaybeLater}
                    disabled={isSubmitting || isLoading}
                  >
                    Maybe later
                  </Button>
                  <Button
                    type="submit"
                    className="w-[219px] bg-black text-white hover:bg-gray-800"
                    disabled={isSubmitting || isLoading}
                  >
                    {isSubmitting
                      ? 'Processing...'
                      : isLoading
                        ? 'Loading...'
                        : 'Upgrade now'}
                  </Button>
                </div>
              </>
            ) : (
              <Button
                type="button"
                onClick={handleMaybeLater}
                disabled={isSubmitting || isLoading}
              >
                OK
              </Button>
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
